/**
 * App.tsx — Root application component
 *
 * Wires together all React providers in the correct order:
 * 1. ThemeProvider    — RTL/LTR direction on document root
 * 2. AuthProvider     — JWT lifecycle, role detection
 * 3. QueryClientProvider — TanStack Query
 * 4. WebSocketProvider — WS connection lifecycle
 * 5. SessionProvider  — Active session state
 * 6. AppRouter        — React Router BrowserRouter + route tree
 *
 * Also registers the api.ts token getter so the fetch wrapper can
 * inject Authorization headers without a circular import.
 *
 * Note: The legacy apiClient.ts in lib/apiClient.ts continues to work
 * for existing note pages (F-404). It reads from localStorage under
 * 'ci_auth_token'. Those pages will be migrated to AuthContext in a
 * future sprint.
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { queryClient, registerTokenGetter } from '@/lib/api';
import { WebSocketBridge } from '@/components/WebSocketBridge';
import { AppRouter } from '@/router';

// ---------------------------------------------------------------------------
// Token bridge: connects AuthContext.getValidToken → api.ts registerTokenGetter
// ---------------------------------------------------------------------------

function TokenBridge() {
  const { getValidToken } = useAuth();

  useEffect(() => {
    registerTokenGetter(getValidToken);
  }, [getValidToken]);

  return null;
}

// ---------------------------------------------------------------------------
// Inner app: has access to AuthContext
// ---------------------------------------------------------------------------

function InnerApp() {
  return (
    <>
      <TokenBridge />
      <WebSocketProvider>
        <WebSocketBridge />
        <SessionProvider>
          <AppRouter />
        </SessionProvider>
      </WebSocketProvider>
    </>
  );
}

// ---------------------------------------------------------------------------
// Root App
// ---------------------------------------------------------------------------

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <InnerApp />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
